import { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import { Link, useNavigate } from "react-router-dom";
import {
  FiUserPlus,
  FiCheckCircle,
  FiArrowRight,
  FiChevronRight,
  FiShield,
  FiUsers,
  FiGlobe
} from "react-icons/fi";
import {
  MdVerifiedUser,
  MdBusinessCenter,
  MdSettings,
  MdOutlineWorkspacePremium,
  MdGroups,
  MdTrendingUp
} from "react-icons/md";
import { TbCertificate } from "react-icons/tb";
import { RiTimeLine } from "react-icons/ri";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChatBot from "../components/ChatBot";
import "../styles/RegisterSolicitorTour.css";

export default function RegisterSolicitorTour() {
  const navigate = useNavigate();
  const [brandName, setBrandName] = useState("");

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/site-settings`);
        const data = await res.json();

        // fallback safety
        setBrandName(data?.brandName || "");
      } catch (err) {
        console.error("Failed to load site settings", err);
      }
    };

    fetchSiteSettings();
  }, []);


  const steps = [
    {
      title: "Signup Initiation",
      description: "Click on Signup to begin the registration process",
      icon: <FiUserPlus className="step-icon-inner" />,
      time: "1 min"
    },
    {
      title: "Account Credentials",
      description: "Enter your email and create a secure password",
      icon: <FiShield className="step-icon-inner" />,
      time: "1 min"
    },
    {
      title: "Account Verification",
      description: "Verify your account using OTP sent to your email",
      icon: <MdVerifiedUser className="step-icon-inner" />,
      time: "2 mins"
    },
    {
      title: "Role Selection",
      description: "Select 'Solicitor' as your professional role",
      icon: <MdOutlineWorkspacePremium className="step-icon-inner" />,
      time: "1 min"
    },
    {
      title: "SRA Registration",
      description: "Enter your valid SRA Number for verification",
      icon: <TbCertificate className="step-icon-inner" />,
      time: "1 min"
    },
    {
      title: "SRA Verification",
      description: "Automatic verification of your SRA credentials",
      icon: <FiCheckCircle className="step-icon-inner" />,
      time: "1 min"
    },
    {
      title: "Subscription Setup",
      description: "Choose and activate your subscription plan",
      icon: <FiCheckCircle className="step-icon-inner" />,
      time: "1 min"
    },
    {
      title: "Profile Completion",
      description: "Complete your personal and professional details",
      icon: <FiUsers className="step-icon-inner" />,
      time: "3 mins"
    },
    {
      title: "Practice Areas",
      description: "Add your legal specializations and expertise",
      icon: <MdBusinessCenter className="step-icon-inner" />,
      time: "2 mins"
    },
    {
      title: "Workspace Setup",
      description: "Configure your workplace and consultation preferences",
      icon: <MdSettings className="step-icon-inner" />,
      time: "2 mins"
    },
    {
      title: "Activation Complete",
      description: "Registration successful! Your account is now active",
      icon: <FiCheckCircle className="step-icon-inner" />,
      time: "1 min"
    }
  ];

  const features = [
    {
      icon: <MdVerifiedUser className="solicitor-feature-icon" />,
      title: "SRA Verified",
      description: "Automatically verify your professional credentials using your SRA number"
    },
    {
      icon: <MdBusinessCenter className="solicitor-feature-icon" />,
      title: "Professional Profile",
      description: "Build a comprehensive legal profile showcasing your expertise"
    },
    {
      icon: <MdSettings className="solicitor-feature-icon" />,
      title: "Workspace Setup",
      description: "Personalize your consultation settings and workplace preferences"
    }
  ];

  return (
    <div className="solicitor-tour-wrapper">
      <Header />

      {/* HERO SECTION */}
      <section className="solicitor-hero-section">
        <div className="solicitor-hero-container">
          <div className="solicitor-hero-content">
            <span className="solicitor-hero-badge">For Legal Professionals</span>
            <h1 className="solicitor-hero-title">
              Join <span className="solicitor-hero-accent">{brandName}</span> as a Solicitor
            </h1>
            <p className="solicitor-hero-description">
              Activate your professional account and connect with clients through our
              trusted legal network. Complete onboarding in under 15 minutes.
            </p>

            {/* ADDED: Quick Stats Row */}
            <div className="solicitor-hero-stats-row">
              <div className="hero-stat-item">
                <MdGroups className="hero-stat-icon" />
                <div className="hero-stat-content">
                  <span className="hero-stat-number">500+</span>
                  <span className="hero-stat-label">Active Solicitors</span>
                </div>
              </div>
              <div className="hero-stat-item">
                <FiGlobe className="hero-stat-icon" />
                <div className="hero-stat-content">
                  <span className="hero-stat-number">UK Wide</span>
                  <span className="hero-stat-label">Network Coverage</span>
                </div>
              </div>
              <div className="hero-stat-item">
                <MdTrendingUp className="hero-stat-icon" />
                <div className="hero-stat-content">
                  <span className="hero-stat-number">40% More</span>
                  <span className="hero-stat-label">Client Reach</span>
                </div>
              </div>
            </div>

            <div className="solicitor-hero-actions">
              <button
                className="solicitor-cta-btn primary"
                onClick={() => navigate("/signup")}
              >
                <FiUserPlus className="solicitor-btn-icon" />
                <span>Start Registration</span>
                <FiChevronRight className="solicitor-btn-arrow" />
              </button>

              <button
                className="solicitor-cta-btn outline"
                onClick={() => document.getElementById("process-steps").scrollIntoView({ behavior: 'smooth' })}
              >
                View Registration Process
              </button>
            </div>

            {/* ADDED: Trust Indicators */}
            <div className="solicitor-trust-indicators">
              <div className="trust-item">
                <FiCheckCircle className="trust-icon" />
                <span>No Upfront Fees</span>
              </div>
              <div className="trust-item">
                <FiCheckCircle className="trust-icon" />
                <span>24/7 Support</span>
              </div>
              <div className="trust-item">
                <FiCheckCircle className="trust-icon" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>

          {/* ANIMATED PROCESS VISUAL */}
          <div className="solicitor-process-visual">
            <div className="process-arc-animation">
              <div className="arc-circle">
                <div className="arc-progress"></div>
                <div className="arc-center">
                  <span className="arc-percentage">100%</span>
                  <span className="arc-label">Verified</span>
                </div>
              </div>
              <div className="process-stats-grid">
                <div className="process-stat">
                  <div className="stat-number">11</div>
                  <div className="stat-label">Steps</div>
                </div>
                <div className="process-stat">
                  <div className="stat-number">15</div>
                  <div className="stat-label">Minutes</div>
                </div>
                <div className="process-stat">
                  <div className="stat-number">100%</div>
                  <div className="stat-label">Secure</div>
                </div>
              </div>

              {/* ADDED: Additional Visual Element */}
              {/* <div className="visual-quote">
                <TbBuildingSkyscraper className="quote-icon" />
                <p>"Join the fastest-growing legal network in the UK"</p>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* REGISTRATION PROCESS */}
      <section id="process-steps" className="solicitor-process-section">
        <div className="solicitor-section-header">
          <h2 className="solicitor-section-title">Registration Process</h2>
          <p className="solicitor-section-subtitle">
            Follow our guided onboarding to activate your solicitor account
          </p>
        </div>

        <div className="solicitor-process-grid">
          {steps.map((step, index) => (
            <div key={index} className="solicitor-step-card">
              <div className="step-card-header">
                <div className="step-number-wrapper">
                  <div className="step-number">{index + 1}</div>
                  <div className="step-time">
                    <RiTimeLine className="time-icon" />
                    <span>{step.time}</span>
                  </div>
                </div>
                <div className="step-icon-circle">
                  {step.icon}
                </div>
              </div>
              <div className="step-card-content">
                <h3 className="step-card-title">{step.title}</h3>
                <p className="step-card-description">{step.description}</p>
              </div>
              <div className="step-progress-indicator">
                <div
                  className="step-progress-bar"
                  style={{ width: `${(index + 1) * 10}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="solicitor-benefits-section">
        <div className="solicitor-section-header">
          <h2 className="solicitor-section-title">Professional Benefits</h2>
          <p className="solicitor-section-subtitle">
            What you get as a {brandName} verified solicitor
          </p>
        </div>

        <div className="solicitor-benefits-grid">
          {features.map((feature, index) => (
            <div key={index} className="solicitor-benefit-card">
              <div className="benefit-icon-container">
                {feature.icon}
              </div>
              <h3 className="benefit-title">{feature.title}</h3>
              <p className="benefit-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="solicitor-final-cta">
        <div className="cta-container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Begin Your Journey?</h2>
            <p className="cta-description">
              Join hundreds of solicitors already providing legal services through {brandName}
            </p>

            <div className="cta-actions">
              <Link to="/signup" className="solicitor-cta-btn primary large">
                <span>Register Now</span>
                <FiArrowRight className="solicitor-btn-arrow" />
              </Link>
            </div>
          </div>

          <div className="cta-assurances">
            <div className="assurance-item">
              <FiCheckCircle className="assurance-icon" />
              <span>Secure & Confidential</span>
            </div>
            <div className="assurance-divider"></div>
            <div className="assurance-item">
              <FiCheckCircle className="assurance-icon" />
              <span>No Setup Fees</span>
            </div>
            <div className="assurance-divider"></div>
            <div className="assurance-item">
              <FiCheckCircle className="assurance-icon" />
              <span>Dedicated Support</span>
            </div>
          </div>
        </div>
      </section>
      <ChatBot />
      <Footer />
    </div>
  );
}